/**
 * Simple Legacy Book readiness — people, memories, photos, preview.
 */
import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Check, ChevronRight, ImageIcon, Mic, Users } from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '@/lib/utils';
import { CORE_EMOTIONAL_LINES, CORE_EMPTY_STATES } from '../../content/coreJourneyContent';
import { deriveCoreJourneyStepStatus, useTreeCoreProgress } from '../../hooks/useTreeCoreProgress';
import { legacyBookPreviewHrefFromId, treeLegacyBookPreviewHref } from '../../utils/legacyBookPreview';
import { personHref } from '../../utils/prettyArchiveLinks';
import { buildTreePeopleAddPersonUrl } from '../../utils/archiveNavigation';
import { LEGACY_BOOK_HTML_PREVIEW } from '../../config/testerRelease';

function ReadinessRow({ icon: Icon, label, done, hint }) {
  return (
    <div
      className={cn(
        'flex items-center gap-3 rounded-xl border px-4 py-3',
        done ? 'border-[hsl(88_18%_78%)]/70 bg-[hsl(88_24%_96%)]/40' : 'border-[hsl(38_14%_88%)]/80 bg-white/60',
      )}
    >
      <span
        className={cn(
          'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg',
          done ? 'bg-[hsl(88_18%_88%)] text-[hsl(88_18%_28%)]' : 'bg-[hsl(42_28%_96%)] text-[#8b7355]',
        )}
      >
        {done ? <Check className="h-4 w-4" aria-hidden /> : <Icon className="h-4 w-4" aria-hidden />}
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-[hsl(24_12%_18%)]">{label}</p>
        {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
      </div>
    </div>
  );
}

export function CoreLegacyBookReadiness({ treeId, onOpenFullProgress, className }) {
  const { progress, loading } = useTreeCoreProgress(treeId);
  const status = useMemo(() => deriveCoreJourneyStepStatus(progress), [progress]);
  const r = status.readiness;

  const previewHref = progress?.firstPersonId
    ? legacyBookPreviewHrefFromId(progress.firstPersonId)
    : null;

  const readyCount = [r.people, r.memories, r.photos, r.preview].filter(Boolean).length;

  return (
    <section
      className={cn(
        'mb-6 rounded-2xl border border-[hsl(38_16%_82%)]/90 bg-gradient-to-br from-[hsl(42_30%_100%)] to-[hsl(40_22%_97%)] p-5 sm:p-6',
        className,
      )}
      data-testid="core-legacy-book-readiness"
    >
      <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-[#8b7355]">Legacy Book</p>
      <h2 className="mt-1 font-serif text-2xl font-light text-[#1c1917]">How close you are</h2>
      <p className="mt-2 max-w-xl text-sm leading-relaxed text-[#5c534a]">{CORE_EMOTIONAL_LINES.bookNotPerfect}</p>

      {loading ? (
        <p className="mt-4 text-sm text-muted-foreground">Checking your archive…</p>
      ) : (
        <>
          <div className="mt-5 grid gap-2 sm:grid-cols-2">
            <ReadinessRow icon={Users} label="People added" done={r.people} hint={r.people ? 'Someone is in your archive' : 'Add at least one loved one'} />
            <ReadinessRow icon={Mic} label="Memories saved" done={r.memories} hint={r.memories ? 'Stories or voice notes preserved' : 'Save one memory to begin'} />
            <ReadinessRow icon={ImageIcon} label="Photos added" done={r.photos} hint={r.photos ? 'Images linked to memories or documents' : 'A photo brings stories to life'} />
            <ReadinessRow icon={BookOpen} label="Ready to preview" done={r.preview} hint={r.preview ? 'You can open a preview now' : CORE_EMPTY_STATES.legacyBookNotReady.description} />
          </div>

          <div className="mt-5 flex flex-wrap gap-2">
            {LEGACY_BOOK_HTML_PREVIEW ? (
              <Button asChild variant="secondary" className="rounded-full">
                <Link to={treeLegacyBookPreviewHref(treeId)} data-testid="core-readiness-tree-book-preview">
                  Preview keepsake book
                  <ChevronRight className="ml-1 h-4 w-4" aria-hidden />
                </Link>
              </Button>
            ) : null}
            {previewHref ? (
              <Button asChild className="rounded-full">
                <Link to={previewHref} data-testid="core-readiness-preview-book">
                  Preview Legacy Book
                  <ChevronRight className="ml-1 h-4 w-4" aria-hidden />
                </Link>
              </Button>
            ) : (
              <Button asChild variant="outline" className="rounded-full">
                <Link to={buildTreePeopleAddPersonUrl(treeId)} data-testid="core-readiness-add-person">
                  Add a loved one first
                </Link>
              </Button>
            )}
            {!r.memories && progress?.firstPersonId ? (
              <Button asChild variant="outline" className="rounded-full">
                <Link to={`${personHref({ id: progress.firstPersonId })}?view=stories`}>
                  Save a memory
                </Link>
              </Button>
            ) : null}
            {typeof onOpenFullProgress === 'function' && readyCount > 0 ? (
              <Button type="button" variant="ghost" className="rounded-full text-muted-foreground" onClick={onOpenFullProgress}>
                View detailed progress
              </Button>
            ) : null}
          </div>
        </>
      )}
    </section>
  );
}

export default CoreLegacyBookReadiness;
